import React from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';

import { VerifyResultItem } from 'stores/TrainingQuestionStoreAdapter';

type Props = {
  index: number;
  item: VerifyResultItem;
};

/** judge0 の Accepted は status.id==3。それ以外で ok=false は実行エラー系。 */
const isRuntimeError = (item: VerifyResultItem) => !item.ok && item.statusId !== 3;

/**
 * 行単位の簡易差分ハイライト。
 * 期待と実際を行で突き合わせ、一致しない行を色付けする。
 */
const renderDiffLines = (text: string, other: string, kind: 'expected' | 'actual') => {
  const lines = text.split('\n');
  const otherLines = other.split('\n');
  return lines.map((line, i) => {
    const differs = line !== (otherLines[i] ?? '');
    return (
      <Box
        key={i}
        component="span"
        sx={{
          display: 'block',
          px: 0.5,
          bgcolor: differs
            ? kind === 'expected'
              ? 'rgba(63, 185, 80, 0.18)'
              : 'rgba(248, 81, 73, 0.18)'
            : 'transparent',
        }}
      >
        {line === '' ? '\u00A0' : line}
      </Box>
    );
  });
};

const OutputBox: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        mt: 0.25,
        p: 1,
        borderRadius: 1,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        fontSize: 12,
        maxHeight: 220,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {children}
    </Box>
  </Box>
);

/**
 * 検証結果のテストケース風カード（入力=arguments / 期待出力 / 実際の出力 / OK-NGバッジ）。
 * NG時は期待と実際の差分をハイライトし、実行エラーは「実行エラー」として区別表示する。
 */
const VerifyResultCard: React.FC<Props> = ({ index, item }) => {
  const runtimeError = isRuntimeError(item);
  return (
    <Box
      sx={{
        border: 1,
        borderColor: item.ok ? 'success.main' : 'error.main',
        borderLeft: 4,
        borderLeftColor: item.ok ? 'success.main' : 'error.main',
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Chip size="small" label={`テストケース ${index + 1}`} sx={{ borderRadius: 1 }} />
        {item.ok ? (
          <Chip
            size="small"
            icon={<CheckCircleIcon />}
            label="OK"
            color="success"
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
        ) : runtimeError ? (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="実行エラー"
            color="warning"
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            icon={<CancelIcon />}
            label="NG"
            color="error"
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {item.statusDescription}
        </Typography>
      </Stack>

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          入力（引数）
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {item.arguments ?? '(引数なし)'}
        </Typography>
      </Box>

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <OutputBox label="期待出力">
            {item.ok
              ? item.expectedStdout
              : renderDiffLines(item.expectedStdout, item.actualStdout, 'expected')}
          </OutputBox>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <OutputBox label="実際の出力">
            {item.ok
              ? item.actualStdout
              : renderDiffLines(item.actualStdout, item.expectedStdout, 'actual')}
          </OutputBox>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VerifyResultCard;
